import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import Layout from '../components/Layout/Layout';

// Hooks personalizados
import { usePricing } from '../hooks/usePricing';
import { useUserQuota } from '../hooks/useUserQuota';
import { useYapePayment } from '../hooks/useYapePayment';

// Componentes
import QuotaStatusHeader from '../components/Purchase/QuotaStatusHeader';
import DocumentSelector from '../components/Purchase/DocumentSelector';
import PriceDetails from '../components/Purchase/PriceDetails';
import DiscountTiers from '../components/Purchase/DiscountTiers';
import YapeModal from '../components/Purchase/YapeModal';

const ComprarDocumentos = () => {
  const { user } = useAuth();
  const [loading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('yape');
  
  // Hooks personalizados
  const { config, priceInfo, loadingConfig, configError, calculatingPrice, documentCount, setDocumentCount } = usePricing();
  const { userQuota, quotaInfo, quotaStatus, quotaClasses } = useUserQuota(user);
  const { 
    showYapeModal, 
    yapePurchaseData, 
    confirmationCode, 
    setConfirmationCode, 
    receiptFile, 
    submittingYape, 
    handleFileUpload, 
    initiateYapePayment, 
    handleYapeSubmit, 
    closeYapeModal 
  } = useYapePayment();

  const handlePurchase = async () => {
    // Solo Yape está disponible por ahora
    if (paymentMethod === 'yape') {
      initiateYapePayment({
        documentCount,
        totalAmount: priceInfo.final_price,
        originalPrice: priceInfo.original_price,
        discount: priceInfo.savings,
        discountPercent: priceInfo.discount_percent,
        qrCodeUrl: "/qr.jpeg"
      });
      return;
    }

    // Métodos no disponibles
    toast.error('Este método de pago no está disponible aún. Por favor, usa Yape.');
  };

  // Mostrar loading mientras se carga la configuración
  if (loadingConfig) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <div className="mt-2">Cargando configuración de precios...</div>
          </div>
        </div>
      </Layout>
    );
  }

  // Mostrar error si no hay configuración
  if (!config || configError) {
    return (
      <Layout>
        <div className="alert alert-danger text-center">
          <h4>⚠️ Servicio de Compras No Disponible</h4>
          <p>No se puede conectar con el servidor de precios. Las compras están temporalmente deshabilitadas.</p>
          <p>Por favor, inténtalo más tarde o contacta al soporte técnico.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-3">
        {/* Header responsivo */}
        <div className="row mb-3">
          <div className="col-12">
            {/* En desktop: lado a lado */}
            <div className="d-none d-lg-flex align-items-center justify-content-between">
              <div>
                <h1 className="h3 fw-bold text-dark mb-1">
                  <i className="bi bi-cart3 me-2 text-primary"></i>
                  Comprar Documentos
                </h1>
                <p className="text-muted mb-0">Aumenta tu cuota de documentos</p>
              </div>
              
              {/* Estado de cuota integrado */}
              <QuotaStatusHeader 
                userQuota={userQuota}
                quotaStatus={quotaStatus}
                quotaClasses={quotaClasses}
                quotaInfo={quotaInfo}
              />
            </div>

            {/* En móvil: apilado */}
            <div className="d-lg-none">
              <div className="text-center mb-3">
                <h1 className="h4 fw-bold text-dark mb-1">
                  <i className="bi bi-cart3 me-2 text-primary"></i>
                  Comprar Documentos
                </h1>
                <p className="text-muted mb-0 small">Aumenta tu cuota de documentos</p>
              </div>
              
              {/* Estado de cuota centrado en móvil */}
              <div className="d-flex justify-content-center">
                <QuotaStatusHeader 
                  userQuota={userQuota}
                  quotaStatus={quotaStatus}
                  quotaClasses={quotaClasses}
                  quotaInfo={quotaInfo}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3">
          {/* Columna central - Selector documentos, precios y descuentos */}
          <div className="col-12 col-lg-8 order-1 order-lg-1">
            {/* Título del panel de selección */}
            <div className="d-flex align-items-center mb-3">
              <div className="bg-primary text-white rounded-circle p-2 me-3 flex-shrink-0">
                <i className="bi bi-1-circle-fill"></i>
              </div>
              <div>
                <h4 className="mb-0 text-primary fw-bold">Configura tu Pedido</h4>
                <small className="text-muted d-none d-sm-block">Selecciona la cantidad y revisa los descuentos disponibles</small>
              </div>
            </div>

            {/* Selector de cantidad y precios */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-4">
                <DocumentSelector 
                  documentCount={documentCount}
                  setDocumentCount={setDocumentCount}
                  config={config}
                />

                <PriceDetails 
                  priceInfo={priceInfo}
                  calculatingPrice={calculatingPrice}
                />
              </div>
            </div>

            {/* Descuentos disponibles */}
            <div className="card border-0 shadow-sm">
              <div className="card-body p-3">
                <h6 className="text-success mb-3">
                  <i className="bi bi-percent-circle me-2"></i>
                  🎯 Descuentos por volumen
                </h6>
                <DiscountTiers 
                  priceInfo={priceInfo}
                  config={config}
                  documentCount={documentCount}
                />
              </div>
            </div>
          </div>

          {/* Columna derecha - Resumen de compra y método de pago */}
          <div className="col-12 col-lg-4 order-2 order-lg-2">
            {/* Título del panel de checkout */}
            <div className="d-flex align-items-center mb-3">
              <div className="bg-success text-white rounded-circle p-2 me-3 flex-shrink-0">
                <i className="bi bi-2-circle-fill"></i>
              </div>
              <div>
                <h4 className="mb-0 text-success fw-bold">Finalizar Compra</h4>
                <small className="text-muted d-none d-sm-block">Revisa tu pedido y elige el método de pago</small>
              </div>
            </div>

            {/* Resumen de compra */}
            {priceInfo && (
              <div className="card border-primary shadow-sm mb-3">
                <div className="card-header bg-primary text-white py-2">
                  <h6 className="card-title mb-0">
                    <i className="bi bi-receipt me-2"></i>
                    Resumen de Compra
                  </h6>
                </div>
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Documentos:</span>
                    <strong>{documentCount.toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Precio base:</span>
                    <span>S/ {priceInfo.original_price}</span>
                  </div>
                  {priceInfo.discount_percent > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-success">Descuento:</span>
                      <span className="text-success">-S/ {priceInfo.savings}</span>
                    </div>
                  )}
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between">
                    <strong>Total:</strong>
                    <strong className="text-primary h5 mb-0">S/ {priceInfo.final_price}</strong>
                  </div>
                  <div className="small text-muted text-center mt-2">
                    S/ {priceInfo.price_per_document} por documento
                  </div>
                </div>
              </div>
            )}

            {/* Método de pago */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-3">
                <h6 className="mb-3">
                  <i className="bi bi-credit-card me-2"></i>
                  Método de Pago
                </h6>

                {/* Mensaje informativo */}
                <div className="alert alert-info py-2 small mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Por ahora solo está disponible <strong>Yape</strong>. Próximamente habilitaremos tarjetas y PayPal.
                </div>
                
                <div className="mb-3">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      id="yape"
                      value="yape"
                      checked={paymentMethod === 'yape'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="yape">
                      <i className="bi bi-phone me-2 text-success"></i>
                      <strong>Yape</strong>
                      <span className="badge bg-success ms-2 small">Disponible</span>
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      id="tarjeta"
                      value="tarjeta"
                      checked={paymentMethod === 'tarjeta'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled
                    />
                    <label className="form-check-label text-muted" htmlFor="tarjeta">
                      <i className="bi bi-credit-card me-2"></i>
                      Tarjeta
                      <span className="badge bg-secondary ms-2 small">Próximamente</span>
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="paymentMethod"
                      id="paypal"
                      value="paypal"
                      checked={paymentMethod === 'paypal'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled
                    />
                    <label className="form-check-label text-muted" htmlFor="paypal">
                      <i className="bi bi-paypal me-2"></i>
                      PayPal
                      <span className="badge bg-secondary ms-2 small">Próximamente</span>
                    </label>
                  </div>
                </div>

                {/* Botón de compra */}
                <button
                  className="btn btn-primary w-100"
                  onClick={handlePurchase}
                  disabled={loading || !priceInfo || calculatingPrice || paymentMethod !== 'yape'}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Procesando...
                    </>
                  ) : calculatingPrice ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Calculando...
                    </>
                  ) : paymentMethod !== 'yape' ? (
                    <>
                      <i className="bi bi-lock me-2"></i>
                      Método no disponible
                    </>
                  ) : (
                    <>
                      <i className="bi bi-phone me-2"></i>
                      Pagar con Yape  S/ {priceInfo?.final_price || '0'}
                    </>
                  )}
                </button>

                <div className="mt-2 text-center">
                  <small className="text-muted">
                    <i className="bi bi-shield-check me-1"></i>
                    Transacción cifrada SSL
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal de Yape */}
        <YapeModal
          showModal={showYapeModal}
          yapePurchaseData={yapePurchaseData}
          confirmationCode={confirmationCode}
          setConfirmationCode={setConfirmationCode}
          receiptFile={receiptFile}
          submittingYape={submittingYape}
          onFileUpload={handleFileUpload}
          onSubmit={handleYapeSubmit}
          onClose={closeYapeModal}
          user={user}
        />
      
      <style>{`
        .transform-scale-105 {
          transform: scale(1.05) !important;
        }
        
        .transition-all {
          transition: all 0.3s ease;
        }
        
        .custom-slider {
          height: 15px;
          border-radius: 10px;
          background: linear-gradient(90deg, #e9ecef 0%, #007bff 100%);
          position: relative;
          outline: none;
        }
        
        .custom-slider::-webkit-slider-thumb {
          appearance: none;
          height: 25px;
          width: 25px;
          border-radius: 50%;
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,123,255,0.3);
          transition: all 0.2s ease;
        }
        
        .custom-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(0,123,255,0.5);
        }
        
        .custom-slider::-moz-range-thumb {
          height: 25px;
          width: 25px;
          border-radius: 50%;
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,123,255,0.3);
          transition: all 0.2s ease;
        }
        
        .card {
          transition: all 0.3s ease;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
        
        .bg-success {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
        }
        
        .border-success {
          border-color: #28a745 !important;
        }
      `}</style>
      </div>
    </Layout>
  );
};

export default ComprarDocumentos;
